import React from 'react';
import { Grid, GridRow as Row, Cell } from '@kizi/shared-components';
import { Div, DivProps } from '@kizi/core-components';
import {
  ComponentDescriptorOrArray,
  getRendererFromDescriptor,
} from '@kizi/comparator-core';

export interface BasePageLayoutProps extends DivProps {
  header?: React.ReactNode;
  promoSection?: React.ReactNode;
  content?: React.ReactNode;
  footer?: React.ReactNode;
  subHeader?: React.ReactNode;
  sideNav?: React.ReactNode;
  navPosition?: 'left' | 'right';
  contentStyles?: any;
  divHeight?: number | string;
  sideNavProps?: Object;
}

export const BasePageLayout: React.FC<BasePageLayoutProps> = ({
  header,
  promoSection,
  content,
  sideNav,
  footer,
  subHeader,
  navPosition = 'right',
  contentStyles,
  divHeight,
  sideNavProps = {},
  ...divProps
}) => {
  const isNavRight = navPosition === 'right';
  const contentOrder = isNavRight ? 0 : 1;
  const sideNavOrder = isNavRight ? 1 : 0;
  const contentProps = sideNav
    ? {
        span: [4, 8, 9],
        pl: isNavRight ? 0 : [0, 0, 0, 70],
        pr: isNavRight ? [0, 0, 0, 70] : 0,
      }
    : {
        gridColumn: ['1/11', '1/11', ' 3/11'],
      };

  const sideNavCell = sideNav ? (
    <Cell
      display={['none', 'none', 'none', 'block']}
      order={[0, 0, sideNavOrder]}
      span={[0, 0, 0, 3]}
      mb={[3, 3, 0]}
      variant="sidenav"
      {...sideNavProps}
    >
      {sideNav}
    </Cell>
  ) : null;

  return (
    <Grid {...divProps} cols={[4, 4, 8, 12]}>
      <Row>
        <Cell span={12}>{header}</Cell>
      </Row>
      <Row>
        <Cell span={12}>{promoSection}</Cell>
      </Row>
      <Row>
        <Cell
          mx={[null, null, null, 'auto']}
          px={[4, 4, 4, 0]}
          span={12}
          width={[null, null, null, 1180]}
        >
          <Grid cols={[4, 4, 8, 12]}>
            <Row>
              <Cell span={[12, 12, 12, 12]}>
                {subHeader ? (
                  subHeader
                ) : (
                  <Div height={divHeight ? Number(divHeight) : 56} />
                )}
              </Cell>
            </Row>
            <Row>
              <Cell
                span={[12, 12, 12, 9]}
                order={[1, 1, contentOrder]}
                {...contentProps}
                {...contentStyles}
              >
                {content}
              </Cell>

              {sideNavCell}
            </Row>
          </Grid>
        </Cell>
      </Row>
      <Row>{footer}</Row>
    </Grid>
  );
};

export interface PageLayoutProps extends DivProps {
  header?: ComponentDescriptorOrArray;
  promoSection?: ComponentDescriptorOrArray;
  content?: ComponentDescriptorOrArray;
  footer?: ComponentDescriptorOrArray;
  subHeader?: ComponentDescriptorOrArray;
  sideNav?: ComponentDescriptorOrArray;
  navPosition?: 'left' | 'right';
  contentStyles?: any;
  divHeight?: number | string;
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  header,
  promoSection,
  content,
  footer,
  subHeader,
  sideNav,
  navPosition,
  contentStyles,
  divHeight,
  ...divProps
}) => {
  const headerJsx = getRendererFromDescriptor(header);
  const promoSectionJsx = getRendererFromDescriptor(promoSection);
  const sideNavJsx = getRendererFromDescriptor(sideNav);
  const footerJsx = getRendererFromDescriptor(footer);
  const subHeaderJsx = getRendererFromDescriptor(subHeader);
  const contentJsx = getRendererFromDescriptor(content);

  return (
    <BasePageLayout
      header={headerJsx}
      promoSection={promoSectionJsx}
      sideNav={sideNavJsx}
      footer={footerJsx}
      subHeader={subHeaderJsx}
      content={contentJsx}
      navPosition={navPosition}
      contentStyles={contentStyles}
      divHeight={divHeight}
      {...divProps}
    />
  );
};
